import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { AdminUrlBuilder } from '../../../../../../shared/admin/api/admin-url-builder';
import { AbstractFormComponent } from '../../../../../../shared/crud/component/form.component';
import {
    InventoryCategory,
    InventoryOption,
    InventoryOptionNotDeleted,
    SubPaper,
    SubPhotopass,
} from '../../models/inventory-option.model';
import { ProductEditForm } from './inventory-edit.form';

@Component({
    templateUrl: './inventory-datatable-edit.component.html',
    styleUrls: ['./inventory-datatable-edit.component.scss'],
})
export class InventoryEditStockModal extends AbstractFormComponent implements OnInit {
    @Input() productName: string;
    @Input() externalId: string = null;
    @Input() category: InventoryCategory;
    @Input() campaignUuid: string;
    @Input() campaignLanguage: string;
    @Input() stockProductId: number;
    @Input() subcategory?: string;
    @Input() stockProducts?: { id: number; product: any }[];

    @Output() onProductEdited = new EventEmitter<any>();

    originalExternalId: string;
    isExternalIdChanged = false;

    subPaper = SubPaper;
    subPhotopass = SubPhotopass;
    selectedPaperOrPhotopass: string = '';
    subProducts: InventoryOption[] = [];

    constructor(
        public modalRef: BsModalRef,
        httpClient: HttpClient,
        urlBuilder: AdminUrlBuilder,
        toastrService: ToastrService,
        router: Router,
        route: ActivatedRoute,
        formBuilder: ProductEditForm
    ) {
        super(formBuilder, httpClient, toastrService, router, route, urlBuilder);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.formMode = 'create';
        this.initialSettings();

        if (this.category === InventoryCategory.product) {
            this.httpClient
                .get<any>(`${this.urlBuilder.buildUrl(this.getUrl())}/campaign/${this.campaignUuid}/products`)
                .subscribe(
                    (response: any) => {
                        this.subProducts = this.mapProductResponseToInventoryOptions(response);
                        const filteredValues: string[] = this.subProducts
                            .filter((product) => product.checked)
                            .map((product) => product.value);
                        this.form.patchValue({ products: filteredValues });
                    },
                    (error: any) => {}
                );
            return;
        }

        this.initialSectionChange(this.subcategory);
    }

    protected getUrl(): string {
        return '/stock-item';
    }

    protected onSubmit(response: any): void {
        this.toastrService.success('Edited');
        this.onProductEdited.emit(response);
        this.channel.next(this.record);
        this.record = {};
        this.form.reset();
    }

    private initialSettings(): void {
        this.form.patchValue({ campaignUuid: this.campaignUuid });
        this.form.patchValue({ name: this.productName });
        this.form.patchValue({ category: this.category });
        this.form.patchValue({ externalId: this.externalId });
        this.originalExternalId = this.externalId;
    }

    private parseLabel(itemName: string): string {
        try {
            const parsedLabel = JSON.parse(itemName);
            let label: string;
            if (parsedLabel[this.campaignLanguage]) {
                label = parsedLabel[this.campaignLanguage];
            } else if (parsedLabel.en) {
                label = parsedLabel.en;
            } else {
                label = parsedLabel[Object.keys(parsedLabel)[0]];
            }
            return label;
        } catch (e) {
            return itemName;
        }
    }

    private mapProductResponseToInventoryOptions(response: any): InventoryOptionNotDeleted[] {
        const stockProductMap = new Map(this.stockProducts.map((item) => [item.product.id, item.product.uuid]));

        return response.map((item) => {
            const label: string = this.parseLabel(item.name);

            const uuid = stockProductMap.get(item.id);

            return {
                label: label,
                value: item.uuid,
                checked: uuid ? true : false,
                isUsed: uuid ? false : item.isUsed,
                isDeleted: !!item.deleteDate.datetime,
            };
        });
    }

    checkIfChanged(origin: string): void {
        if (origin === 'externalId') {
            this.isExternalIdChanged = this.externalId !== this.originalExternalId;
            this.form.patchValue({ externalId: this.externalId });
        }
    }

    initialSectionChange(subcategory: string): void {
        this.form.patchValue({ subcategory: subcategory });

        if (this.category === InventoryCategory.paper) {
            this.subPaper = this.subPaper.map((option) => {
                if (option.value === subcategory) {
                    this.selectedPaperOrPhotopass = option.value;
                    return { ...option, checked: true };
                }
                return option;
            });
            return;
        }
        if (this.category === InventoryCategory.photopass) {
            this.subPhotopass = this.subPhotopass.map((option) => {
                if (option.value === subcategory) {
                    this.selectedPaperOrPhotopass = option.value;
                    return { ...option, checked: true };
                }
                return option;
            });
            return;
        }
    }

    onSelectionChange(selection: string | string[]) {
        if (this.category === InventoryCategory.product) {
            this.form.patchValue({ products: selection });
            return;
        }
        this.form.patchValue({ subcategory: selection });
    }

    submit(): void {
        this.formMode = 'create';
        if (this.form.invalid) {
            this.toastrService.error('Form contains incorrect data');
            return;
        }

        this.httpClient
            .put(`${this.urlBuilder.buildUrl(this.getUrl())}/${this.stockProductId}`, this.form.value)
            .subscribe(
                (response: any) => {
                    this.toastrService.success('Product edited');
                    this.modalRef.hide();
                    this.onProductEdited.emit();
                },
                (error: any) => {
                    this.toastrService.error(error.error ? error.error.message : 'Product has been not edited');
                }
            );
    }
}
